import { useCallback, useContext } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import { AppActionsAPI } from 'api/AppActionsAPI';

/**
 * Returns a stable `logAppAction(action)` callback bound to the current user
 * — call it from a button handler after any "disabled / nothing to do" guard,
 * right before the real work starts. Fire-and-forget: never await, never
 * blocks the click it's logging.
 */
export const useLogAppAction = (): ((action: string) => void) => {
  const { cur_user, tenant_access, isWhiteLabel } = useContext(GlobalContext);

  return useCallback(
    (action: string) => {
      // logAction swallows its own errors internally (see AppActionsAPI),
      // so this never rejects -- intentionally not awaited/void'd.
      // tenant_access.domain is the resolved host for this session (the
      // tenant's custom domain on a white-label/JWT login, otherwise
      // window.location.host -- see GlobalProvider's initKeycloakUser). Fall
      // back to window.location.host directly: the AWS-token login path
      // (GlobalProvider's awsSuccessCallback) never calls setTenantAccess,
      // so tenant_access.domain stays undefined for the lifetime of an
      // AWS-authenticated session -- without this fallback every action
      // logged from that flow would record a null domain.
      // tenant_access.name is only populated for a white-label session (see
      // GlobalProvider's initKeycloakUser -- the tenant fetch is gated
      // behind `if (resolvedIsWhiteLabel)`), the same value App.tsx uses for
      // the browser tab title, so it's undefined/omitted here for any
      // non-white-label session.
      AppActionsAPI.logAction(
        action,
        cur_user,
        tenant_access?.domain || window.location.host,
        isWhiteLabel,
        tenant_access?.name,
      );
    },
    [cur_user, tenant_access, isWhiteLabel],
  );
};

export default useLogAppAction;
